import { useMemo } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';

// Target fields for import with German labels
const TARGET_FIELDS = [
	{ key: 'companyName', label: 'Firmenname', required: true },
	{ key: 'websiteUrl', label: 'Website / Domain', required: true },
	{ key: 'addressStreet', label: 'Straße', required: false },
	{ key: 'addressPostal', label: 'PLZ', required: false },
	{ key: 'addressCity', label: 'Stadt', required: false },
];

export default function ColumnMappingStep({ headers, previewRows, columnMapping, onMappingChange, onNext, onBack }) {
	// Check if mapping is valid (both companyName and websiteUrl must be mapped)
	const isValid = useMemo(() => {
		return Boolean(columnMapping.companyName) && Boolean(columnMapping.websiteUrl);
	}, [columnMapping]);

	// Get available columns (not yet mapped to other fields)
	const getAvailableColumns = (currentField) => {
		const usedColumns = Object.entries(columnMapping)
			.filter(([field, col]) => field !== currentField && col)
			.map(([, col]) => col);

		return headers.filter((h) => !usedColumns.includes(h));
	};

	const handleMappingChange = (targetField, csvColumn) => {
		onMappingChange({
			...columnMapping,
			[targetField]: csvColumn || null,
		});
	};

	// Get preview value for a mapped column
	const getPreviewValue = (csvColumn, rowIndex) => {
		if (!csvColumn || !previewRows[rowIndex]) return '-';
		return previewRows[rowIndex][csvColumn] || '-';
	};

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-text mb-1">Spalten zuordnen</h3>
				<p className="text-sm text-bgBase-800">Ordnen Sie die CSV-Spalten den entsprechenden Feldern zu</p>
			</div>

			{/* Mapping Table */}
			<div className="border border-gray-200 rounded-lg overflow-hidden">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-4 py-3 text-left text-xs font-medium text-bgBase-800 uppercase tracking-wider w-1/3">
								Zielfeld
							</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-bgBase-800 uppercase tracking-wider w-1/3">
								CSV-Spalte
							</th>
							<th className="px-4 py-3 text-left text-xs font-medium text-bgBase-800 uppercase tracking-wider w-1/3">
								Vorschau
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{TARGET_FIELDS.map((field) => (
							<tr key={field.key}>
								<td className="px-4 py-3 whitespace-nowrap">
									<span className="text-sm font-medium text-text">{field.label}</span>
									{field.required && <span className="ml-1 text-xs text-warning-500">*</span>}
								</td>
								<td className="px-4 py-3">
									<Select
										value={columnMapping[field.key] || ''}
										onChange={(e) => handleMappingChange(field.key, e.target.value)}
										className="w-full"
									>
										<option value="">-- Nicht zuordnen --</option>
										{getAvailableColumns(field.key).map((header) => (
											<option key={header} value={header}>
												{header}
											</option>
										))}
									</Select>
								</td>
								<td className="px-4 py-3">
									<span className="text-sm text-bgBase-800 truncate block max-w-[200px]">
										{getPreviewValue(columnMapping[field.key], 0)}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Validation Hint */}
			<p className="text-xs text-bgBase-800">* Firmenname und Website / Domain müssen zugeordnet werden</p>

			{/* Preview Table */}
			{previewRows.length > 0 && Object.values(columnMapping).some((v) => v) && (
				<div className="mt-6">
					<h4 className="text-sm font-medium text-text mb-2">
						Datenvorschau (erste {previewRows.length} Zeilen)
					</h4>
					<div className="border border-gray-200 rounded-lg overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 text-sm">
							<thead className="bg-gray-50">
								<tr>
									{TARGET_FIELDS.filter((f) => columnMapping[f.key]).map((field) => (
										<th
											key={field.key}
											className="px-3 py-2 text-left text-xs font-medium text-bgBase-800 uppercase"
										>
											{field.label}
										</th>
									))}
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{previewRows.map((row, rowIndex) => (
									<tr key={rowIndex}>
										{TARGET_FIELDS.filter((f) => columnMapping[f.key]).map((field) => (
											<td
												key={field.key}
												className="px-3 py-2 whitespace-nowrap text-text max-w-[200px] truncate"
											>
												{row[columnMapping[field.key]] || '-'}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Navigation Buttons */}
			<div className="flex justify-between">
				<Button variant="outline" onClick={onBack}>
					Zurück
				</Button>
				<Button variant="solid" onClick={onNext} disabled={!isValid}>
					Weiter
				</Button>
			</div>
		</div>
	);
}

ColumnMappingStep.propTypes = {
	headers: PropTypes.arrayOf(PropTypes.string).isRequired,
	previewRows: PropTypes.arrayOf(PropTypes.object).isRequired,
	columnMapping: PropTypes.object.isRequired,
	onMappingChange: PropTypes.func.isRequired,
	onNext: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
};
